import type { MaybeRef } from 'vue';
import { usePageJsonLd } from './usePageJsonLd';
import type { ContactPointConfig } from './usePageJsonLd';
import {
  matchPathSegment,
  resolveSiteBaseUrl,
  type PageSeoData,
} from './useJsonLdShared';

const DEFAULT_DOMAIN = 'https://www.toogoodtogo.com';

interface ContactUsJsonLdOptions {
  pageData: PageSeoData;
  /** Optional override - if omitted, derives from runtimeConfig + localePath */
  contactPoint?: MaybeRef<ContactPointConfig | null>;
}

/**
 * Builds and injects JSON-LD structured data for the Contact Us page.
 * Uses ContactPage schema with mainEntity ContactPoint.
 * Only runs when the current route is the contact-us page.
 */
export function useContactUsJsonLd(options: ContactUsJsonLdOptions) {
  const config = useRuntimeConfig();
  const localePath = useLocalePath();

  const contactPoint = computed(() => {
    const provided = unref(options.contactPoint);
    if (provided) {
      return provided;
    }

    const domain = resolveSiteBaseUrl(
      config.public?.domain as string | undefined,
      DEFAULT_DOMAIN,
    );
    return {
      contactType: 'customer support' as const,
      email: config.public?.contact?.email || undefined,
      url: config.public?.contact?.supportUrl || `${domain}${localePath('/contact-us')}`,
    };
  });

  usePageJsonLd({
    pageData: options.pageData,
    isCurrentPage: path => matchPathSegment(path, 'contact-us', { allowNested: true }),
    pageType: 'ContactPage',
    defaultTitle: 'Contact us',
    pagePath: '/contact-us',
    scriptKey: 'contact-us-jsonld',
    contactPoint,
  });
}
